/**
 * Utility functions for geocoding operations using OpenStreetMap
 */

/**
 * OpenStreetMap search result type
 */
interface NominatimSearchResult {
  place_id: string;
  display_name: string;
  lat: string;
  lon: string;
  address?: {
    house_number?: string;
    road?: string;
    suburb?: string;
    city?: string;
    municipality?: string;
    postcode?: string;
    country?: string;
    country_code?: string;
  };
}

/**
 * Format address components into a clean, readable format
 * Format: "Road HouseNumber, Postcode City, Country"
 * @param address Address components from Nominatim
 * @returns Formatted address string or null if insufficient data
 */
function formatAddress(address: NominatimResponse["address"]): string | null {
  if (!address) return null;

  const parts: string[] = [];

  // Street address: "Road HouseNumber" or just "Road"
  if (address.road) {
    if (address.house_number) {
      parts.push(`${address.road} ${address.house_number}`);
    } else {
      parts.push(address.road);
    }
  }

  // City with postcode: "Postcode City" or just "City"
  const city = address.city || address.municipality;
  if (city) {
    if (address.postcode) {
      parts.push(`${address.postcode} ${city}`);
    } else {
      parts.push(city);
    }
  }

  // Country
  if (address.country) {
    parts.push(address.country);
  }

  // Return formatted address if we have at least street or city
  if (parts.length > 0) {
    return parts.join(", ");
  }

  return null;
}

/**
 * Search for addresses using OpenStreetMap Nominatim
 * @param query Search query (e.g., "Bispeengen Odense")
 * @returns Promise<NominatimSearchResult[]> Array of search results
 */
export async function searchAddresses(query: string): Promise<NominatimSearchResult[]> {
  if (query.length < 3) {
    return [];
  }

  try {
    const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&addressdetails=1&accept-language=da,en&countrycodes=dk&limit=5`, {
      headers: {
        "User-Agent": "LoppeHub/1.0 (https://loppehub.dk)",
      },
    });

    if (response.ok) {
      const results: NominatimSearchResult[] = await response.json();
      return results || [];
    }

    return [];
  } catch (error) {
    console.error("OpenStreetMap search error:", error);
    return [];
  }
}

/**
 * OpenStreetMap Nominatim API response type
 */
interface NominatimResponse {
  display_name?: string;
  address?: {
    house_number?: string;
    road?: string;
    suburb?: string;
    city?: string;
    municipality?: string;
    postcode?: string;
    country?: string;
    country_code?: string;
  };
  lat?: string;
  lon?: string;
  error?: string;
}

/**
 * Reverse geocode coordinates to get a human-readable address using OpenStreetMap Nominatim
 * @param lat Latitude
 * @param lng Longitude
 * @returns Promise<string> Formatted address or coordinates as fallback
 */
export async function reverseGeocode(lat: number, lng: number): Promise<string> {
  try {
    const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1&accept-language=da,en&zoom=18`, {
      headers: {
        "User-Agent": "LoppeHub/1.0 (https://loppehub.copilau.me)", // Be polite to OSM
      },
    });

    if (response.ok) {
      const data: NominatimResponse = await response.json();

      if (data && data.address) {
        // Format address as: "Road HouseNumber, City, Country"
        const formatted = formatAddress(data.address);
        if (formatted) {
          return formatted;
        }
      }

      // Fallback to display_name if address formatting fails
      if (data && data.display_name) {
        return data.display_name;
      }
    }

    // Fallback to coordinates if no address found
    return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
  } catch (error) {
    console.error("OpenStreetMap reverse geocoding error:", error);
    // Fallback to coordinates on error
    return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
  }
}
